import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Info, CheckCircle, Sparkle } from "@phosphor-icons/react";

/**
 * Read-only coach view of a coachee's sectioned profile (Zoekprofiel,
 * Werkgeversprofiel of Werknemerprofiel).
 *
 * Renders every section + every field. Empty fields show a subtle "—".
 * No edit, no AI buttons — coaches only inspect what the coachee filled.
 *
 * Props:
 *   coacheeId   — string
 *   coacheeName — string
 *   endpoint    — e.g. "search-profile", "employer-profile", "employee-profile"
 *   icon        — Phosphor icon component
 *   title       — short label like "Zoekprofiel"
 *   description — one-line description for the header card.
 */
export default function ProfileCoachPanel({
  coacheeId, coacheeName, endpoint, icon: Icon, title, description,
}) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get(`/coachees/${coacheeId}/${endpoint}`);
        if (!cancelled) setDoc(data);
      } catch (err) {
        if (!cancelled) setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [coacheeId, endpoint]);

  const sections = doc?.sections || [];
  const data = doc?.data || {};
  const filledCount = sections.reduce(
    (acc, s) => acc + (s.fields || []).filter((f) => (data[f.key] || "").toString().trim()).length,
    0,
  );
  const totalFields = sections.reduce((acc, s) => acc + (s.fields || []).length, 0);
  const aiCount = doc?.ai_prefill_count ?? 0;

  return (
    <div className="space-y-4 max-w-3xl" data-testid={`${endpoint}-coach-panel`}>
      <div className="neuro-card-bold p-5 bg-white">
        <div className="flex items-start gap-3">
          {Icon ? <Icon size={26} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" /> : null}
          <div className="flex-1">
            <h3 className="font-display text-lg font-bold tracking-tight">{title}</h3>
            <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
              {description || `Read-only weergave van het profiel van ${coacheeName || "de coachee"}.`}
            </p>
            <div className="mt-3 flex items-center gap-4 text-xs text-zinc-500 flex-wrap" data-testid={`${endpoint}-coach-stats`}>
              <span>Status: <strong className="text-zinc-900">{filledCount === 0 ? "Nog niet ingevuld" : filledCount === totalFields ? "Volledig" : "Gedeeltelijk"}</strong></span>
              <span>· {filledCount} / {totalFields} velden ingevuld</span>
              {aiCount > 0 && (
                <span className="inline-flex items-center gap-1">
                  <Sparkle size={12} className="text-[#1E88E5]" /> AI-prefill gebruikt: {aiCount} ×
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm" data-testid={`${endpoint}-coach-error`}>{error}</div>
      )}

      {loading ? (
        <div className="text-zinc-500 text-sm">Laden...</div>
      ) : sections.length === 0 ? (
        <div className="neuro-card p-6 bg-white">
          <div className="flex items-start gap-3">
            <Info size={22} className="text-[#1E88E5] shrink-0 mt-0.5" />
            <p className="text-sm text-zinc-600">Geen structuur ontvangen van de server.</p>
          </div>
        </div>
      ) : filledCount === 0 ? (
        <div className="neuro-card p-6 bg-white" data-testid={`${endpoint}-coach-empty`}>
          <div className="flex items-start gap-3">
            <Info size={22} className="text-[#1E88E5] shrink-0 mt-0.5" />
            <div>
              <h4 className="font-display text-base font-bold tracking-tight">Nog niet ingevuld</h4>
              <p className="text-sm text-zinc-600 mt-1">
                {coacheeName || "De coachee"} heeft het {title.toLowerCase()} nog niet ingevuld.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4" data-testid={`${endpoint}-coach-sections`}>
          {sections.map((section) => (
            <ProfileSectionView key={section.id} section={section} data={data} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProfileSectionView({ section, data }) {
  const fields = section.fields || [];
  const filled = fields.filter((f) => (data[f.key] || "").toString().trim()).length;
  return (
    <div className="neuro-card p-5 bg-white" data-testid={`profile-section-${section.id}`}>
      <div className="flex items-center justify-between mb-3 gap-3">
        <h4 className="font-display text-base font-bold tracking-tight flex items-center gap-2">
          {filled === fields.length && fields.length > 0 && <CheckCircle size={18} className="text-[#43A047]" />}
          {section.title}
        </h4>
        <span className="text-xs text-zinc-500 shrink-0">{filled} / {fields.length}</span>
      </div>
      <dl className="space-y-3">
        {fields.map((field) => {
          const value = (data[field.key] || "").toString();
          return (
            <div key={field.key}>
              <dt className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{field.label}</dt>
              <dd
                className={"text-sm whitespace-pre-wrap leading-relaxed " + (value.trim() ? "text-zinc-900" : "text-zinc-400 italic")}
                data-testid={`profile-field-${field.key}`}
              >
                {value.trim() || "— niet ingevuld —"}
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}
